import React, { useState, useEffect, useRef } from 'react'
import { FaTrash } from 'react-icons/fa'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  CustomCalendar,
  InputNumeroInteiro,
  InputNumeroDecimal,
  InputText,
  CustomInputTextArea,
  SectionTitle,
} from '../../../../../../components'
import { getEmissoresByTps } from '../../../../../../services/api/https/settings/emissor-cte.http'
import { postEmitCteSemNota } from '../../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../../services/api/https/settings/cfop.http'
import { CTE_TIPOS_OUTROS_DOCUMENTOS } from '../../../../../../constants/cte-sem-nota'
import ValoresFiscaisCte from '../../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  VALORES_FISCAIS_INICIAIS,
  erroNumeroOrdemColeta,
  erroValoresFiscais,
  montarComponentesValor,
  toNum,
} from '../../ValoresFiscaisCte/valoresFiscaisCte.util'
import useFormularioCte from '../../Partes/useFormularioCte'
import SecaoPartes from '../../Partes/SecaoPartes'
import SecaoTomador from '../../Partes/SecaoTomador'
import SecaoPrestacao from '../../Partes/SecaoPrestacao'
import SecaoCarga from '../../Partes/SecaoCarga'
import useEmissorUnico from '../../Partes/useEmissorUnico'
import { dataParaIso } from './cteSemNota.util'

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

export default function ModalEmitirCteSemNota({
  selectedTp,
  visible,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [componentes, setComponentes] = useState([])
  const proximaChaveDocumento = useRef(1)
  const [emissores, setEmissores] = useState([])
  const [cfops, setCfops] = useState([])
  const [documentos, setDocumentos] = useState([])
  const form = useFormularioCte({
    visible,
    onError,
    origemRemetente: 'empresa',
    origemDestinatario: 'manual',
  })

  const novoDocumento = () => ({
    key: proximaChaveDocumento.current++,
    tipo_documento: '99',
    descricao: '',
    numero: '',
    data_emissao: null,
    valor: null,
  })

  // Reinicia o formulário a cada abertura (uma linha de documento tipo 99 por padrão)
  useEffect(() => {
    if (!visible) return
    setState(inicialState)
    setComponentes([])
    setDocumentos([novoDocumento()])
  }, [visible])

  useEffect(() => {
    async function fetchListas() {
      try {
        const [listaEmissores, listaCfops] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
        ])
        setEmissores(listaEmissores)
        setCfops(
          listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
        )
      } catch (error) {
        onError(error)
      }
    }
    if (selectedTp) fetchListas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTp])

  const emissorSelecionado = emissores.find(item => item.id === state.emissor_id) || null
  useEmissorUnico({
    visible,
    emissores,
    selecionado: emissorSelecionado,
    onSelecionar: emissor => setState(prevState => ({ ...prevState, emissor_id: emissor.id })),
  })
  const cfopSelecionado = cfops.find(item => item.id === state.cfop_id) || null
  const ufEmissor = emissorSelecionado ? emissorSelecionado.uf : null
  const mesmaUf = Boolean(form.ufInicio && form.ufFim && form.ufInicio === form.ufFim)
  const tiposDocumento = CTE_TIPOS_OUTROS_DOCUMENTOS.filter(
    tipo => !tipo.somenteIntraestadual || mesmaUf,
  )

  // Helpers - documentos
  const onDocumentoChange = (item, campo, valor) => {
    setDocumentos(prevState =>
      prevState.map(doc => (doc.key === item.key ? { ...doc, [campo]: valor } : doc)),
    )
  }

  const onAdicionarDocumento = () => setDocumentos(prevState => [...prevState, novoDocumento()])

  const onRemoverDocumento = item => {
    setDocumentos(prevState =>
      prevState.length === 1 ? prevState : prevState.filter(doc => doc.key !== item.key),
    )
  }

  // Validação (espelho das regras do backend; a primeira inconsistência é devolvida)
  const validar = () => {
    if (!selectedTp) return 'Selecione o Operador Logístico'
    if (!emissorSelecionado) return 'O campo Emissor é obrigatório'
    const erroOrdemColeta = erroNumeroOrdemColeta(state.numero_ordem_coleta)
    if (erroOrdemColeta) return erroOrdemColeta
    const erroFormulario = form.validar({ cfop: cfopSelecionado, ufEmissor })
    if (erroFormulario) return erroFormulario

    if (!documentos.length) return 'Informe ao menos um documento'
    for (let i = 0; i < documentos.length; i++) {
      const doc = documentos[i]
      const posicao = `Documento ${i + 1}`
      if (!doc.tipo_documento) return `${posicao}: selecione o tipo`
      if (!String(doc.numero || '').trim()) return `${posicao}: informe o número`
      if (doc.tipo_documento === '99' && !String(doc.descricao || '').trim()) {
        return `${posicao}: a descrição é obrigatória para o tipo Outros`
      }
      if (['59', '65'].includes(doc.tipo_documento) && !mesmaUf) {
        return `${posicao}: CF-e SAT e NFC-e só são aceitos em prestação dentro da mesma UF`
      }
      if (doc.data_emissao && !dataParaIso(doc.data_emissao)) {
        return `${posicao}: data de emissão inválida`
      }
    }

    const erroFiscal = erroValoresFiscais(state)
    if (erroFiscal) return erroFiscal
    if (state.observacoes && state.observacoes.length > 2000) {
      return 'O campo Observações deve ter no máximo 2000 caracteres'
    }
    return null
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      const erro = validar()
      if (erro) throw new Error(erro)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCteSemNota(selectedTp, {
        ...state,
        ...form.montarPayload(),
        outros_documentos: documentos.map(doc => ({
          tipo_documento: doc.tipo_documento,
          descricao: String(doc.descricao || '').trim() || null,
          numero: String(doc.numero).trim(),
          data_emissao: dataParaIso(doc.data_emissao),
          valor: toNum(doc.valor),
        })),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave()
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe sem Nota Fiscal'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-col-12">
          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '10px 15px',
              borderRadius: '5px',
              marginBottom: '10px',
            }}
          >
            CTe emitido sem vínculo com Notas Fiscais: os documentos da carga vão como "Outros"
            (Declaração / Outros) e os dados de remetente, destinatário, prestação e carga são
            informados aqui.
          </div>
        </div>

        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores}
            value={emissorSelecionado}
            label="Emissor:*"
            filter={true}
            onChange={value => setState(prevState => ({ ...prevState, emissor_id: value.id }))}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>

        <SecaoPartes form={form} selectedTp={selectedTp} onError={onError} />
        <SecaoTomador form={form} selectedTp={selectedTp} onError={onError} />
        <SecaoPrestacao
          form={form}
          cfops={cfops}
          cfopSelecionado={cfopSelecionado}
          onCfopChange={cfop =>
            setState(prevState => ({ ...prevState, cfop_id: cfop ? cfop.id : null }))
          }
          ufEmissor={ufEmissor}
        />
        <SecaoCarga
          carga={form.carga}
          onChange={form.setCarga}
          onUnidadeChange={form.onUnidadeChange}
        />

        {/* Documentos (infOutros) */}
        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Documentos da Carga" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 5,
            }}
          >
            <span style={{ color: 'gray', fontSize: 12 }}>
              A SEFAZ exige ao menos um documento. Descrição obrigatória no tipo Outros; CF-e SAT e
              NFC-e só em prestação dentro da mesma UF.
            </span>
            <Btn onClick={onAdicionarDocumento}>Adicionar documento</Btn>
          </div>
        </div>
        {documentos.map((doc, index) => (
          <React.Fragment key={doc.key}>
            <div className="p-md-3 p-col-12">
              <CustomDropdown
                optionLabel="label"
                options={tiposDocumento}
                value={tiposDocumento.find(tipo => tipo.value === doc.tipo_documento) || null}
                label={`Tipo do documento ${index + 1}:*`}
                onChange={tipo => onDocumentoChange(doc, 'tipo_documento', tipo.value)}
              />
            </div>
            <div className="p-md-4 p-col-12">
              <InputText
                label={doc.tipo_documento === '99' ? 'Descrição:*' : 'Descrição:'}
                marginTop={10}
                value={doc.descricao}
                maxLength={100}
                placeholder="Ex.: Apuracao de frete"
                onChange={value => onDocumentoChange(doc, 'descricao', value)}
              />
            </div>
            <div className="p-md-2 p-col-12">
              <InputText
                label="Número:*"
                marginTop={10}
                value={doc.numero}
                maxLength={20}
                placeholder="Ex.: AP-2030-01"
                onChange={value => onDocumentoChange(doc, 'numero', value)}
              />
            </div>
            <div className="p-md-2 p-col-12">
              <CustomCalendar
                label="Data emissão:"
                marginTop={10}
                value={doc.data_emissao}
                maxDate={new Date()}
                appendTo={document.body}
                onChange={value => onDocumentoChange(doc, 'data_emissao', value)}
              />
            </div>
            <div
              className="p-md-1 p-col-12"
              style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 15 }}
            >
              {documentos.length > 1 && (
                <FaTrash
                  style={{ cursor: 'pointer' }}
                  color="red"
                  onClick={() => onRemoverDocumento(doc)}
                />
              )}
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Valor do documento:"
                marginTop={0}
                placeholder="Ex.: 125.000,00"
                value={doc.valor}
                onChange={value => onDocumentoChange(doc, 'valor', value)}
              />
            </div>
            <div className="p-md-9 p-col-12" />
          </React.Fragment>
        ))}

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>
    </CustomModal>
  )
}
